import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';

import { propTypes } from '../../util/types';

import { sendVerificationEmail, hasCurrentUserErrors, markIntegrityCompletionNotificationSeenDuck, markEmployerAnswerSeen } from '../../ducks/user.duck';
import { logout, authenticationInProgress } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import {
  updateLinkedConsultantDuck,
  approveAccessRequestDuck,
  rejectAccessRequestDuck,
} from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { fetchNotifications, markLinkSeen, markNoticeSeen } from '../../ducks/linkedContacts.duck';
import { userTypeOf } from '../../util/userType';
import { teamRoleOf } from '../../util/teamRole';

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));

/**
 * Topbar container component, which is connected to Redux Store.
 * @component
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const TopbarContainerComponent = props => {
  const { notificationCount = 0, isOnboardingForm = false, ...rest } = props;

  return <Topbar notificationCount={notificationCount} isOnboardingForm={isOnboardingForm} {...rest} />;
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated and isLoggedInAs
  const { isAuthenticated, isLoggedInAs, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserNotificationCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    profileAccessRequests,
    integrityCompletionNotifications,
    profileListing,
  } = state.user;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));

  let isOnboardingForm = false;

  const publicData = currentUser?.attributes?.profile?.publicData || {};

  const { firstListingId, addedUserJoined } = publicData;
  const role = teamRoleOf(currentUser);
  const userType = userTypeOf(currentUser);

  const isCandidate = userType === 'candidate';

  const isAgencyAdmin = userType === 'agency' && (role === 'Admin' || role === undefined);

  const isCustomerAdmin = userType === 'customer' && (role === 'Admin' || role === undefined);

  // const needsFirstListing = (isCandidate || isAgencyAdmin || isCustomerAdmin) && firstListingId === undefined;
  // // const isNonAdminNotJoined = role !== 'Admin' && (addedUserJoined === undefined || addedUserJoined === false);
  // const isNonAdminNotJoined = (currentUser && role !== 'Admin' && addedUserJoined === undefined && !isCandidate);


  // if (needsFirstListing || isNonAdminNotJoined) {
  //   isOnboardingForm = true;
  // }
  
  const isOnboardedUser = (publicData?.profileListingId && publicData?.addedUserJoined) || publicData?.firstListingId;
  
  if(!isOnboardedUser && !!currentUser) {
    isOnboardingForm = true;
  }

  const conversations = state.InboxPage?.conversations || [];
  const currentUserAdminId = state.InboxPage?.profileListingAdminId || currentUser?.id?.uuid;
  const totalUnreadConversations = conversations.filter(conv => {
    const me = conv.participants?.find(p => p.userId === currentUserAdminId);
    return (me?.unreadCount || 0) > 0;
  }).length;

  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    notificationCount: currentUserNotificationCount,
    totalUnreadConversations,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
    isOnboardingForm,
    linkNotifications: state.linkedContacts.notifications,
    notices: state.linkedContacts.notices,
    profileAccessRequests,
    integrityCompletionNotifications,
    profileListing,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  // A candidate's answer to a link request, then the bell read again.
  onUpdateLinkedConsultant: async params => {
    const result = await dispatch(updateLinkedConsultantDuck(params));
    await dispatch(fetchNotifications());
    return result;
  },
  // A consultant's answer marked seen on its one link, then the bell read again so the next one shows.
  onMarkLinkSeen: async ({ _id }) => {
    const result = await dispatch(markLinkSeen({ _id }));
    await dispatch(fetchNotifications());
    return result;
  },
  onApproveAccessRequest: (params) => dispatch(approveAccessRequestDuck(params)),
  onRejectAccessRequest: (params) => dispatch(rejectAccessRequestDuck(params)),
  onMarkIntegrityCompletionNotificationSeen: (params) =>
    dispatch(markIntegrityCompletionNotificationSeenDuck(params)),
  onMarkEmployerAnswerSeen: answeredAt => dispatch(markEmployerAnswerSeen(answeredAt)),
  onMarkNoticeSeen: id => dispatch(markNoticeSeen(id)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(TopbarContainerComponent);

export default TopbarContainer;
